import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';

import {
  CookiesBar,
  GtmNoScript,
  HeadTags,
  Header,
  initLayout,
  UserCentric,
} from '@catawiki/layout';

import type { LayoutAssets } from '@/shared/helpers/api/assets';

import styles from './Layout.module.scss';

const LayoutFooter = dynamic(
  () => import('./LayoutFooter').then((mod) => mod.Footer),
  { ssr: false }
);

interface Props {
  children: ReactNode;
  data: LayoutAssets;
  hideHeader?: boolean;
  hideFooter?: boolean;
  headerVariant?: 'sellerOnly' | 'register' | 'focused' | 'seller';
  showCookieBanner?: boolean;
}

export const Layout: FC<Props> = ({
  children,
  data,
  hideFooter,
  hideHeader,
  headerVariant = 'sellerOnly',
  showCookieBanner,
}) => {
  const usesLegacyAssets = data.useLayoutHeader !== true;

  useEffect(() => {
    if (!usesLegacyAssets) {
      initLayout();
    }

    document.body.classList.toggle('tw:header-offset', !hideHeader);
  }, [hideHeader, usesLegacyAssets]);

  const legacyHead = useMemo(() => {
    if (!usesLegacyAssets) {
      return null;
    }

    return parse(data.tags.head + data.tags.css + data.third_party.gtm);
  }, [data.tags.css, data.tags.head, data.third_party.gtm, usesLegacyAssets]);

  const headerContent = useMemo(() => {
    if (!usesLegacyAssets) {
      return null;
    }

    return { __html: data.header };
  }, [data.header, usesLegacyAssets]);

  const scriptsContent = useMemo(() => {
    if (!usesLegacyAssets) {
      return null;
    }

    return { __html: data.tags.js };
  }, [data.tags.js, usesLegacyAssets]);

  /* eslint-disable react/no-danger */
  return (
    <>
      <Head>{usesLegacyAssets ? legacyHead : <HeadTags />}</Head>
      {!usesLegacyAssets && <GtmNoScript />}
      {!hideHeader &&
        (usesLegacyAssets ? (
          <div
            id="cw-header-container"
            className={styles.header}
            suppressHydrationWarning
            dangerouslySetInnerHTML={headerContent ?? undefined}
          />
        ) : (
          <div className={styles.header}>
            <Header variant={headerVariant} />
          </div>
        ))}

      {/* Actual page content */}
      {children}
      {/* End actual page content */}
      {!hideFooter && <LayoutFooter imprintOnly />}
      {showCookieBanner &&
        (usesLegacyAssets ? <UserCentric /> : <CookiesBar />)}
      {usesLegacyAssets && scriptsContent && (
        <div
          id="cw-js-container"
          suppressHydrationWarning
          dangerouslySetInnerHTML={scriptsContent}
        />
      )}
    </>
  );
};
